/** A stable row; expanded details expose long names, every tag and the rule. */
import { FC, useId } from 'react';
import { ChevronRight } from 'lucide-react';
import type { DropGenHourFormula } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import { TagChips } from '../../../rules/TagChips';
import { ScoreDetail } from '../../../creative/ranking/results/ScoreDetail';
import { type ListResult, RESULT_LABEL } from './results-model';

const TONE = {
  ranked: 'text-success', 'no-traffic': 'text-muted-foreground',
  excluded: 'text-warning', pending: 'text-muted-foreground',
};

export const ListResultRow: FC<{ row: ListResult; open: boolean; onToggle: () => void; formula?: DropGenHourFormula }> = ({ row, open, onToggle, formula }) => {
  const detailsId = useId();
  const name = row.name ?? `List #${row.wiserId}`;
  return (
    <>
      <tr className={cn('group border-b border-border/50 transition-colors hover:bg-muted/30', row.position === 1 && 'bg-primary/[0.035]')}>
        <td className="px-2 py-2 text-center align-top font-mono text-[11px] tabular-nums text-muted-foreground" title={row.position === 1 ? 'First in this queue' : 'Position in the full queue'}>{row.position}</td>
        <td className="min-w-0 py-1.5 pr-3 align-top">
          <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={detailsId} aria-label={`${open ? 'Hide' : 'Show'} details for ${name}`}
            className="flex w-full min-w-0 items-center gap-1 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <ChevronRight className={cn('h-3 w-3 shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none', open && 'rotate-90')} aria-hidden />
            <span className="min-w-0 truncate text-xs font-medium" title={name}>{name}</span>
          </button>
          {row.tags.length > 0 ? <div className="ml-4 mt-1 overflow-hidden [&>span]:flex-nowrap [&>span>span]:shrink-0 [&>span>span]:whitespace-nowrap" title={row.tags.join(', ')}><TagChips tags={row.tags} max={2} /></div> : null}
        </td>
        <td className="px-2 py-2 text-right align-top font-mono text-xs tabular-nums" aria-label={row.kind === 'ranked' ? undefined : 'Not scored'}>{row.kind === 'ranked' && row.score !== null ? row.score.toFixed(2) : '—'}</td>
        <td className="px-3 py-2 align-top">
          <span className={cn('inline-flex items-center gap-1.5 text-[11px] font-medium', TONE[row.kind])}><span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" aria-hidden />{RESULT_LABEL[row.kind]}</span>
          <span className="mt-0.5 block truncate text-[10px] text-muted-foreground" title={row.reason}>{row.kind === 'ranked' ? `${row.visits?.toLocaleString('en-US') ?? '—'} unique visits` : row.reason}</span>
        </td>
      </tr>
      <tr id={detailsId} hidden={!open}>
        <td colSpan={4} className="border-b border-border/60 bg-muted/20 px-4 py-3">
          <div className="flex flex-col gap-2 text-xs">
            <p className="break-all font-medium">{name}</p>
            <p className="text-muted-foreground">{row.reason}{row.kind === 'excluded' || row.kind === 'no-traffic' ? ' This list stays in the queue after ranked lists, in biggest-first order.' : ''}</p>
            <TagChips tags={row.tags} max={row.tags.length} />
            <p className="text-[11px] text-muted-foreground">List ID {row.wiserId}{row.leadCount !== null ? ` · ${row.leadCount.toLocaleString('en-US')} leads` : ''}{row.visits !== null ? ` · ${row.visits.toLocaleString('en-US')} unique visits in the primary range` : ''}</p>
            {open && row.kind === 'ranked' && row.source && formula ? <ScoreDetail formula={formula} breakdown={row.source.breakdown} metricsByRange={row.source.metricsByRange} unrankedNote={`Formula score ${row.score?.toFixed(2) ?? 'unavailable'}.`} /> : null}
          </div>
        </td>
      </tr>
    </>
  );
};
